import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit, Trash2, X } from "lucide-react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../../../../api";
import "../parametresStyles/ParamConges.css";

const Toggle = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        className={`pcToggle ${checked ? "is-on" : ""}`}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
    >
        <span className="pcToggle__thumb" />
    </button>
);

const ParamTypesContrats = () => {
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState({});
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    const getHeaders = useCallback(() => ({
        uid: sessionStorage.getItem("uid"),
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
        "Content-Type": "application/json",
        Accept: "application/json",
    }), []);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const resp = await axios.get(`${API_BASE_URL}/api/types-contrats`, {
                headers: getHeaders(),
            });
            setItems(resp.data || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Erreur chargement types de contrats");
        } finally {
            setLoading(false);
        }
    }, [getHeaders]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const openModal = (item = null) => {
        setFormErrors({});
        setEditItem(item);
        setForm(item ? {
            code: item.code || "",
            libelle: item.libelle || "",
            description: item.description || "",
            actif: item.actif ?? true,
        } : {
            code: "",
            libelle: "",
            description: "",
            actif: true,
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditItem(null);
        setForm({});
        setFormErrors({});
    };

    const validateForm = () => {
        const errs = {};
        if (!form.code?.trim()) errs.code = "Requis";
        if (!form.libelle?.trim()) errs.libelle = "Requis";
        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        const payload = {
            code: form.code?.trim().toUpperCase(),
            libelle: form.libelle?.trim(),
            description: form.description?.trim() || null,
            actif: form.actif ?? true,
        };

        try {
            if (editItem) {
                await axios.put(`${API_BASE_URL}/api/types-contrats/${editItem.id}`, payload, {
                    headers: getHeaders(),
                });
                Swal.fire({ icon: "success", title: "", text: "Type de contrat mis à jour", timer: 1400, showConfirmButton: false });
            } else {
                await axios.post(`${API_BASE_URL}/api/types-contrats`, payload, {
                    headers: getHeaders(),
                });
                Swal.fire({ icon: "success", title: "", text: "Type de contrat ajouté", timer: 1400, showConfirmButton: false });
            }
            closeModal();
            fetchItems();
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Erreur enregistrement type de contrat";
            setError(message);
            Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonText: "OK", confirmButtonColor: "#d33" });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (item) => {
        Swal.fire({
            title: "Confirmation",
            text: `Voulez-vous vraiment supprimer le type "${item.libelle}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385",
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            setLoading(true);
            try {
                await axios.delete(`${API_BASE_URL}/api/types-contrats/${item.id}`, {
                    headers: getHeaders(),
                });
                setItems((prev) => prev.filter((i) => i.id !== item.id));
                Swal.fire({ icon: "success", title: "", text: "Type de contrat supprimé", timer: 1400, showConfirmButton: false });
            } catch (err) {
                const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Erreur suppression type de contrat";
                setError(message);
                Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonText: "OK", confirmButtonColor: "#d33" });
            } finally {
                setLoading(false);
            }
        });
    };

    const toggleActive = async (item) => {
        const payload = {
            code: item.code,
            libelle: item.libelle,
            description: item.description,
            actif: !item.actif,
        };

        setLoading(true);
        try {
            const resp = await axios.put(`${API_BASE_URL}/api/types-contrats/${item.id}`, payload, {
                headers: getHeaders(),
            });
            setItems((prev) => prev.map((i) => i.id === item.id ? resp.data : i));
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Erreur mise à jour statut");
        } finally {
            setLoading(false);
        }
    };

    const renderModal = () => {
        if (!showModal) return null;
        const isEdit = !!editItem;

        return (
            <div className="pcModal">
                <div className="pcModal__panel" role="dialog" aria-modal="true" style={{ maxWidth: 520 }}>
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            {isEdit ? "Modifier un type de contrat" : "Ajouter un type de contrat"}
                        </div>
                        <button className="pcIconBtn" onClick={closeModal} type="button" aria-label="Fermer" disabled={loading}>
                            <X size={18} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="pcWizGrid">
                            <div className="pcWizField">
                                <label>Code <span className="pcReq">*</span></label>
                                <input
                                    type="text"
                                    className={`pcInput ${formErrors.code ? "is-error" : ""}`}
                                    value={form.code || ""}
                                    onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                                    placeholder="CDI"
                                />
                                {formErrors.code && <span className="pcErrMsg">{formErrors.code}</span>}
                            </div>

                            <div className="pcWizField">
                                <label>Libellé <span className="pcReq">*</span></label>
                                <input
                                    type="text"
                                    className={`pcInput ${formErrors.libelle ? "is-error" : ""}`}
                                    value={form.libelle || ""}
                                    onChange={(e) => setForm((p) => ({ ...p, libelle: e.target.value }))}
                                    placeholder="Contrat à durée indéterminée"
                                />
                                {formErrors.libelle && <span className="pcErrMsg">{formErrors.libelle}</span>}
                            </div>

                            <div className="pcWizField is-full">
                                <label>Description</label>
                                <textarea
                                    className="pcInput pcInput--textarea"
                                    rows={3}
                                    value={form.description || ""}
                                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                    placeholder="Ex: CDI, CDD, Stage, Freelance..."
                                />
                            </div>

                            <div className="pcWizField is-full">
                                <div className="pcInfoBox">
                                    <Toggle checked={!!form.actif} onChange={(v) => setForm((p) => ({ ...p, actif: v }))} disabled={loading} />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 12 }}>Type actif</div>
                                        <div style={{ fontSize: 11, color: "#64748b" }}>Disponible lors de la création des contrats.</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pcModal__footer">
                        <button className="pcBtn pcBtn--ghost" type="button" onClick={closeModal} disabled={loading}>Annuler</button>
                        <button className="pcBtn pcBtn--primary" type="button" onClick={handleSubmit} disabled={loading}>{isEdit ? "Enregistrer" : "Créer"}</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="pcPage">
            <div className="pcHeader">
                <div>
                    <h1>Gestion des types de contrats</h1>
                    {error && <div style={{ marginTop: 8, color: "#b91c1c", fontWeight: 700 }}>Erreur: {error}</div>}
                </div>
            </div>

            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcBody">
                        <div className="pcTopRow">
                            <div>
                                <h2 className="pcTitle">Types de contrats</h2>
                            </div>
                            <button className="pcBtn pcBtn--primary" type="button" onClick={() => openModal()} disabled={loading}>
                                <Plus size={16} /> Ajouter un type de contrat
                            </button>
                        </div>

                        <div className="pcTableWrap">
                            <table className="pcTable">
                                <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Libellé</th>
                                    <th>Description</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr><td colSpan={5} className="pcEmptyCell">Chargement...</td></tr>
                                ) : items.length === 0 ? (
                                    <tr><td colSpan={5} className="pcEmptyCell">Aucun type de contrat configuré</td></tr>
                                ) : items.map((item) => (
                                    <tr key={item.id}>
                                        <td><span className="pcCode" style={{ backgroundColor: "#204385" }}>{item.code}</span></td>
                                        <td><div className="pcStrong">{item.libelle}</div></td>
                                        <td><div className="pcMuted" style={{ fontSize: 12 }}>{item.description || "-"}</div></td>
                                        <td>
                                            <button className={`pcPill ${item.actif ? "is-on" : "is-off"}`} type="button" onClick={() => toggleActive(item)} disabled={loading}>
                                                {item.actif ? "Actif" : "Inactif"}
                                            </button>
                                        </td>
                                        <td className="is-right">
                                            <div className="pcRowActions">
                                                <button className="pcIconBtn" type="button" title="Modifier" onClick={() => openModal(item)} disabled={loading}>
                                                    <Edit size={16} />
                                                </button>
                                                <button className="pcIconBtn pcIconBtn--danger" type="button" title="Supprimer" onClick={() => handleDelete(item)} disabled={loading}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {renderModal()}
        </div>
    );
};

export default ParamTypesContrats;
